'use client';

import { useLocalStorageState } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { useUserNexus } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { ANNOUNCEMENTS } from './registry';
import type { AnnouncementConfig } from './types';

const STORAGE_KEY = 'TOP_ANNOUNCEMENT_CLOSED_IDS';

export function useTopAnnouncement() {
  const { userInfo, subscription, isTeamSpace, countryLevel } = useStore((s) => ({
    userInfo: s.userInfo,
    subscription: s.subscription,
    isTeamSpace: s.isTeamSpace,
    countryLevel: s.countryLevel,
  }));
  const { isSubOneTimeOffer } = useUserNexus();

  const [closedIds, setClosedIds] = useLocalStorageState<string[]>(STORAGE_KEY, {
    defaultValue: [],
    listenStorageChange: true,
  });

  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
  }, []);

  const activeAnnouncement = useMemo<AnnouncementConfig | null>(() => {
    if (now === null) return null;

    const ctx = { userInfo, subscription, isTeamSpace, countryLevel, isSubOneTimeOffer };
    const sorted = [...ANNOUNCEMENTS].sort((a, b) => b.priority - a.priority);

    for (const announcement of sorted) {
      if (announcement.expiresAt && now > announcement.expiresAt.getTime()) continue;
      if (announcement.condition && !announcement.condition(ctx)) continue;
      if (closedIds?.includes(announcement.id)) continue;
      return announcement;
    }

    return null;
  }, [now, userInfo, subscription, isTeamSpace, countryLevel, isSubOneTimeOffer, closedIds]);

  const closeAnnouncement = (id: string) => {
    setClosedIds((prev) => {
      const list = prev ?? [];
      return list.includes(id) ? list : [...list, id];
    });
  };

  return { activeAnnouncement, closeAnnouncement };
}
